import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { Tooltip } from 'antd';
import { TypeUtils } from '../../../common/misc';
import './RenewalSuggestionField.less';

const showMoreLabelWidth = 80;

interface Props {
    fieldLabel: string;
    fieldValue: unknown;
    fieldValuesDelimiter?: string;
    hideShowMoreOption?: boolean;
}

const RenewalSuggestionField: React.FC<Props> = ({ fieldLabel, fieldValue, fieldValuesDelimiter = ', ' }) => {
    const textContainerRef = React.useRef<HTMLDivElement>(null);
    const textRef = React.useRef<HTMLSpanElement>(null);

    const [expanded, setExpanded] = React.useState(false);
    const [visibleText, setVisibleText] = React.useState('');
    const [hiddenText, setHiddenText] = React.useState('');
    const [hiddenCount, setHiddenCount] = React.useState(0);

    const allText: string = React.useMemo(() => {
        if (TypeUtils.isString(fieldValue)) {
            return fieldValue;
        }

        if (TypeUtils.isNumber(fieldValue)) {
            return fieldValue.toString();
        }

        if (TypeUtils.isStringArray(fieldValue)) {
            return fieldValue.join(fieldValuesDelimiter);
        }

        return '';
    }, [fieldValue, fieldValuesDelimiter]);

    React.useEffect(() => {
        // If the field value is not a string array show all text
        if (!TypeUtils.isStringArray(fieldValue)) {
            setExpanded(true);
            setVisibleText(allText);
            return;
        }

        if (expanded) {
            setVisibleText(allText);
            setHiddenText('');
            setHiddenCount(0);
            return;
        }

        if (!textContainerRef.current || !textRef.current) {
            return;
        }

        const container = textContainerRef.current;
        const containerWidth = container.offsetWidth;

        // Reset to full content for measurement
        textRef.current.textContent = allText;
        const fullWidth = textRef.current.scrollWidth;

        // If all content fits, show everything
        if (fullWidth <= containerWidth) {
            setVisibleText(allText);
            setHiddenText('');
            setHiddenCount(0);
            return;
        }

        // Calculate available width (reserving space for more label)
        const availableWidth = containerWidth - showMoreLabelWidth;

        let visibleItems: string[] = [];
        let testText = '';

        // Add items one by one until we exceed available width
        for (let i = 0; i < fieldValue.length; i++) {
            const newTestText = testText ? testText + fieldValuesDelimiter + fieldValue[i] : fieldValue[i];

            textRef.current.textContent = newTestText;
            const newWidth = textRef.current.scrollWidth;

            if (newWidth <= availableWidth) {
                testText = newTestText;
                visibleItems.push(fieldValue[i]);
            } else {
                break;
            }
        }

        const hiddenItems = fieldValue.slice(visibleItems.length);
        setVisibleText(visibleItems.join(fieldValuesDelimiter));
        setHiddenText(hiddenItems.join(fieldValuesDelimiter));
        setHiddenCount(hiddenItems.length);
    }, [allText, expanded, fieldValue, fieldValuesDelimiter]);

    return (
        <div className="renewal-suggestion-field-container">
            <span className="field-label">{fieldLabel}</span>

            <div className="field-value-container" ref={textContainerRef}>
                <span ref={textRef} className={`field-value ${expanded ? 'expanded' : ''}`}>
                    {expanded ? allText : visibleText}
                </span>

                {hiddenCount > 0 && !expanded && (
                    <Tooltip title={hiddenText}>
                        <span
                            className="show-more-label"
                            style={{ width: showMoreLabelWidth }}
                            onClick={() => setExpanded(true)}
                        >
                            and {hiddenCount} more
                        </span>
                    </Tooltip>
                )}
            </div>
        </div>
    );
};

export default observer(RenewalSuggestionField);
